import { useEffect, useRef } from "react";
import { effectivePointCoords, HELP_MAP_TILE_URL, loadLeaflet } from "./help-location-map";

interface LeafletLatLng {
  lat: number;
  lng: number;
}

interface LeafletMarker {
  addTo: (map: LeafletMap) => LeafletMarker;
  on: (event: string, handler: () => void) => void;
  getLatLng: () => LeafletLatLng;
  setLatLng: (latlng: [number, number]) => void;
}

interface LeafletLayer {
  addTo: (map: LeafletMap) => void;
}

interface LeafletMap {
  remove: () => void;
  invalidateSize: () => void;
  setView: (latlng: [number, number], zoom: number) => void;
  getZoom: () => number;
}

interface LeafletApi {
  map: (
    el: HTMLElement,
    opts: {
      center: [number, number];
      zoom: number;
      attributionControl: boolean;
      maxZoom?: number;
    },
  ) => LeafletMap;
  tileLayer: (
    url: string,
    opts: { maxZoom: number; tms: boolean; attribution?: string },
  ) => LeafletLayer;
  marker: (
    latlng: [number, number],
    opts: { draggable: boolean },
  ) => LeafletMarker;
}

function asLeafletApi(value: unknown): LeafletApi | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<LeafletApi>;
  if (typeof candidate.map !== "function") return null;
  return candidate as LeafletApi;
}

interface HelpLeafletMapProps {
  lat: number;
  lon: number;
  onPositionChange: (lat: number, lon: number) => void;
}

export function HelpLeafletMap({ lat, lon, onPositionChange }: HelpLeafletMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onPositionChange);
  callbackRef.current = onPositionChange;

  const latRef = useRef(lat);
  const lonRef = useRef(lon);
  latRef.current = lat;
  lonRef.current = lon;

  const moveMapRef = useRef<((nextLat: number, nextLon: number) => void) | null>(null);
  const { lat: mapLat, lon: mapLon } = effectivePointCoords({ lat, lon });

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let mapInstance: LeafletMap | null = null;

    void loadLeaflet().then((loaded) => {
      const L = asLeafletApi(loaded);
      if (cancelled || !containerRef.current || !L) return;

      const init = effectivePointCoords({
        lat: latRef.current,
        lon: lonRef.current,
      });

      const map = L.map(containerRef.current, {
        center: [init.lat, init.lon],
        zoom: 15,
        maxZoom: 18,
        attributionControl: false,
      });
      mapInstance = map;

      L.tileLayer(HELP_MAP_TILE_URL, {
        maxZoom: 18,
        tms: false,
        attribution:
          '&copy; <a href="https://2gis.ru" target="_blank" rel="noopener noreferrer">2GIS</a>',
      }).addTo(map);

      const marker = L.marker([init.lat, init.lon], { draggable: true }).addTo(map);
      let dragging = false;
      marker.on("dragstart", () => {
        dragging = true;
      });
      marker.on("dragend", () => {
        dragging = false;
        const position = marker.getLatLng();
        callbackRef.current(position.lat, position.lng);
      });

      moveMapRef.current = (nextLat, nextLon) => {
        if (dragging) return;
        const current = marker.getLatLng();
        if (
          Math.abs(current.lat - nextLat) < 0.000001 &&
          Math.abs(current.lng - nextLon) < 0.000001
        ) {
          return;
        }
        marker.setLatLng([nextLat, nextLon]);
        map.setView([nextLat, nextLon], map.getZoom());
      };

      setTimeout(() => map.invalidateSize(), 300);
    });

    return () => {
      cancelled = true;
      moveMapRef.current = null;
      if (mapInstance) {
        mapInstance.remove();
      }
    };
  }, []);

  useEffect(() => {
    moveMapRef.current?.(mapLat, mapLon);
  }, [mapLat, mapLon]);

  return <div ref={containerRef} className="help-point-map" />;
}
